// Match Announcement Feed Item Component
// Displays match announcements and relationship milestones

import React from "react";
import { motion } from "framer-motion";
import {
  FeedItemContent,
  ContentOverlay,
  UserInfo,
  UserAvatar,
  UserDetails,
  Username,
  Timestamp,
  ContentText,
  ContentTitle,
  ContentDescription,
  ActionButtons,
  ActionButton,
  ActionCount,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { BaseFeedItemProps, FeedInteraction } from "../../../types/socialFeed";

export const MatchAnnouncementItem: React.FC<BaseFeedItemProps> = ({
  item,
  onInteraction,
  onUserClick,
  onSwipeModeTrigger,
  showSwipeOption = false,
  isFullScreen = false,
  aspectRatio,
}) => {
  const { content, engagement } = item;

  // Get match announcement display
  const getMatchDisplay = () => {
    if (content.announcement) {
      return {
        icon: "💕",
        title: "New Match!",
        description: `Matched with ${content.announcement.matchedUserId}`,
        compatibilityScore: content.announcement.compatibilityScore,
        sharedInterests: content.announcement.sharedInterests,
        color: "#e91e63",
      };
    }
    return {
      icon: "💕",
      title: "Match Announcement",
      description: "New match found!",
      compatibilityScore: 0,
      sharedInterests: [],
      color: "#e91e63",
    };
  };

  const displayData = getMatchDisplay();

  return (
    <FeedItemContent>
      {/* Background with match gradient */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${displayData.color}20 0%, ${displayData.color}40 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Match Icon */}
        <motion.div
          style={{
            fontSize: "4rem",
            marginBottom: "1rem",
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.6, 
            delay: 0.2,
            type: "spring",
            stiffness: 200 
          }}
        >
          {displayData.icon}
        </motion.div>
      </motion.div>

      {/* Content Overlay */}
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          <ContentTitle style={{ color: displayData.color }}>
            {displayData.title}
          </ContentTitle>
          <ContentDescription>
            {displayData.description}
          </ContentDescription>
          
          {/* Compatibility Score */}
          {displayData.compatibilityScore > 0 && (
            <div style={{ 
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              fontSize: "0.8rem",
            }}>
              <strong>Compatibility:</strong> {displayData.compatibilityScore}%
            </div>
          )}

          {/* Shared Interests */}
          {displayData.sharedInterests.length > 0 && (
            <div style={{
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              fontSize: "0.8rem",
            }}>
              <strong>Shared Interests:</strong> {displayData.sharedInterests.slice(0, 3).join(", ")}
            </div>
          )}
        </ContentText>
      </ContentOverlay>

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{engagement.shares}</ActionCount>

        {/* Send Message Button */}
        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "view",
            timestamp: new Date(),
            metadata: { action: "send-message" }
          })}
        >
          💌
        </ActionButton>
      </ActionButtons>
    </FeedItemContent>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString();
};
